import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { show } from '@intercom/messenger-js-sdk';
import { useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material';

import lastStepImage from '~/assets/images/last-step.png';
import SignUpForm from '~/components/organisms/auth/SignUpForm/SignUpForm';
import { Button } from '~/components/ui/button';
import { Divider } from '~/components/ui/divider/Divider';
import { Text } from '~/components/ui/text';
import { TEMPLATE_SEARCH_PARAM } from '~/constants/anonymousQR';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import { QRCODE_DYNAMIC_MICROSITE_TYPE } from '~/constants/qr-code';
import { COLORS } from '~/styles/colors';

const availableToLinkQrCodeTypes = [
  QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE,
  QRCODE_DYNAMIC_MICROSITE_TYPE.VCARD_PLUS,
] as string[];

interface LastStepLoginProps {
  selectedQR?: number;
  qrCode?: any;
}
export default function LastStepLogin({ selectedQR, qrCode }: LastStepLoginProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const [showConfirmationContent, setShowConfirmationContent] = useState(false);
  const template = searchParams.get(TEMPLATE_SEARCH_PARAM);

  const isTemplateAvailableToLinkToUser = !template || availableToLinkQrCodeTypes.includes(template);

  return (
    <>
      {isDesktop && <StyledPoster src={lastStepImage} alt="privacy policy concept illustration" />}
      <Text variant={isDesktop ? 'h1.5' : 'h3'}>{t('login:popuph1')}</Text>
      {showConfirmationContent ? (
        <Text
          variant={isDesktop ? 'subheader' : 'inputSmall'}
          sx={{ marginTop: '4px', color: COLORS.grey400, textAlign: 'center' }}
        >
          {t('login:confirmationh2')} {t('login:confirmationh3')}
        </Text>
      ) : (
        <Text variant={isDesktop ? 'subheader' : 'inputSmall'} sx={{ marginTop: '4px' }}>
          {t('login:popuph2')}
        </Text>
      )}
      {showConfirmationContent ? (
        <>
          <Divider mb={24} mt={24} width={200} />
          <Text
            variant={isDesktop ? 'subheader' : 'inputSmall'}
            sx={{ color: COLORS.grey400, textAlign: 'center', marginBottom: '24px' }}
          >
            {t('confirmation:p4')}
          </Text>
          <Button type="contained" sx={{ width: '100%' }} onClick={() => setShowConfirmationContent(false)}>
            {t('confirmation:btnText')}
          </Button>
          <Text variant={isDesktop ? 't16Regular' : 'inputSmall'} sx={{ textAlign: 'center', marginTop: '24px' }}>
            {t('confirmation:p5')}
          </Text>
          <StyledContactSupportContainer>
            <Text variant={isDesktop ? 't16Regular' : 'inputSmall'} sx={{ textAlign: 'center' }}>
              {t('confirmation:p6')}
            </Text>
            <StyledContactSupport onClick={show}>{t('confirmation:p6_end_text')}</StyledContactSupport>
          </StyledContactSupportContainer>
        </>
      ) : (
        <StyledSignUpForm
          cameFromPopup
          isAnonCreateQr={isTemplateAvailableToLinkToUser}
          openConfirmationPopup={() => setShowConfirmationContent(true)}
          selectedQR={selectedQR}
          qrCode={qrCode}
        />
      )}
    </>
  );
}

export const StyledPoster = styled('img')(() => ({
  marginBottom: '20px',
  maxWidth: '226px',
}));

export const StyledSignUpForm = styled(SignUpForm)(({ theme }) => ({
  marginBottom: 0,
  marginTop: '24px',
  [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
    maxWidth: '100%',
  },
}));

export const StyledContactSupportContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '3px',
}));

export const StyledContactSupport = styled('button')(({ theme }) => ({
  all: 'unset',
  color: COLORS.primary,
  fontWeight: 500,
  fontSize: '16px',
  cursor: 'pointer',
  [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
    fontSize: '14px',
  },
}));
